import React from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { usePlanzonePermissions } from "@components/permissions";
import { RestrictedArea } from "@scenes/restrictedArea";

import {
  useRetrieveCurrentPlanzone,
  useRetrievePlanzoneSettings,
  useRetrieveProjectListingConfiguration,
  useRetrieveSecondaryCriteriaTemplate,
  useRetrieveStrategicAxes,
} from "@planzone/view-models";
import { ProjectListingConfiguration } from "@planzone/dto";
import { Filter } from "@common/filter";
import { WeightTag } from "@common/weightTag";
import { Language } from "@common/language";
import { AllocationUnit } from "@common/allocationUnit";
import { useRoles } from "@scenes/safeguard";
import { Project } from "./project";
import { AverageWeather } from "./averageWeather";
import { averageIndicators, toFixed } from "./utils";
import { Icon } from "./icon";
import { Props } from "./types";
import { Resizeable } from "./resizeable";
import styles from "./styles.scss";

const fixedColumns = [
  "zone",
  "startDate",
  "endDate",
  "portfolios",
  "tags",
  "status",
  "weather",
  "strategicScore",
  "activityMonitoring",
  "projectManagers",
  // "activityMonitoring_done",
  // "activityMonitoring_delayed",
  // "activityMonitoring_opened",
  "workload",
  // "workload_allocatedTime",
  // "workload_spentTime",
  // "workload_remainingWork",
  "budget",
  // "budget_plannedBudget",
  // "budget_realCosts",
  // "budget_acquiredValue",
];

function ProjectSection({
  header,
  projects,
  finalPlannedValueOfProject,
  proportionOfAccomplishedWork,
  proportionOfDelayedWork,
  proportionOfOpenedWork,
  totalOfRemainingTimeInProject,
  totalOfTimesSpentInProject,
  totalSumOfTimeAllocatedOnTheProject,
  averageOfConsistencyOfStrategicAxes,
  trackingAcquiredValue,
  allocatedBudgetValue,
  plannedMarginBudget,
  realMarginBudget,
  projectManagers,
  trackingRealCost,
  filter,
  loader,
  isOpen,
  currentSort,
  columnSizes = {},
  onColumnSort,
  resizeCol,
  resizeDone = () => {},
}: Props) {
  const { t } = useTranslation();
  const { isAdministrator } = usePlanzonePermissions();
  const { planzone } = useRetrieveCurrentPlanzone();
  const { settings } = useRetrievePlanzoneSettings(planzone);
  const { strategicAxes } = useRetrieveStrategicAxes();
  const { configuration } = useRetrieveProjectListingConfiguration(planzone);
  const { template } = useRetrieveSecondaryCriteriaTemplate(
    planzone.uuid,
    "project",
  );
  const roles = useRoles({});
  const profile =
    configuration.find((p) =>
      p.roles.find((role) =>
        roles.find((r) => r.uuid === role || r.name === role),
      ),
    ) || configuration.length > 0
      ? configuration[0]
      : {
          name: "",
          roles: [],
          elements: [],
        };
  const columns = profile.elements.filter(
    ({ key }) =>
      !![...fixedColumns, ...template.map(({ uuid }) => uuid)].find(
        (criteria) => criteria === key,
      ),
  );
  const colNbr = columns.reduce(
    (count, element) =>
      count + (element.type === "section" ? element.elements.length : 1),
    0,
  );
  const getColSize = (key: string) =>
    key in columnSizes ? columnSizes[key] : key === "name" ? 280 : 100;
  const onSortChange = (columnKey: string) => {
    if (!onColumnSort) return;

    const isSameColumn = currentSort?.columnKey === columnKey;
    const currentDirection = currentSort?.direction;

    const newDirection: "asc" | "desc" | null = isSameColumn
      ? currentDirection === "asc"
        ? "desc"
        : currentDirection === "desc"
          ? null
          : "asc"
      : "asc";

    onColumnSort(columnKey, newDirection);
  };

  return (
    <RestrictedArea areas={["strategicAxes"]}>
      {({ canAccessStrategicAxes }) => (
        <Language>
          {({ currentLanguage }) => (
            <Filter
              filter={filter}
              data={projects}
              extractCriteria={(project) => ({ project })}
            >
              {(filtered) => {
                const indicators = averageIndicators(filtered, template);
                const fixedColMap = {
                  zone: () => <div className={styles.zone} />,
                  startDate: () => <div className={styles.dates} />,
                  endDate: () => <div className={styles.dates} />,
                  portfolios: () => <div className={styles.portfolios} />,
                  tags: () => <div className={styles.tags} />,
                  status: () => <div className={styles.status} />,
                  weather: () => (
                    <div className={styles.weather}>
                      {<AverageWeather projects={filtered} />}
                    </div>
                  ),
                  strategicScore: () => (
                    <div className={styles.strategicScore}>
                      <WeightTag
                        weight={
                          filtered.length > 0
                            ? Math.floor(
                                filtered.reduce(
                                  (
                                    acc,
                                    { averageOfConsistencyOfStrategicAxes },
                                  ) =>
                                    acc + averageOfConsistencyOfStrategicAxes,
                                  0,
                                ) / filtered.length,
                              )
                            : 0
                        }
                        maxWeight={strategicAxes.reduce(
                          (acc, { weight }) => acc + weight * 2,
                          0,
                        )}
                      />
                    </div>
                  ),
                  activityMonitoring_done: () => (
                    <p className={classNames(styles.text, styles.positive)}>
                      {toFixed(
                        typeof proportionOfAccomplishedWork !== "undefined"
                          ? proportionOfAccomplishedWork
                          : indicators.proportionOfAccomplishedWork,
                      )}
                      %
                    </p>
                  ),
                  activityMonitoring_delayed: () => (
                    <p className={classNames(styles.text, styles.warning)}>
                      {toFixed(
                        typeof proportionOfDelayedWork !== "undefined"
                          ? proportionOfDelayedWork
                          : indicators.proportionOfDelayedWork,
                      )}
                      %
                    </p>
                  ),
                  activityMonitoring_opened: () => (
                    <p className={styles.text}>
                      {toFixed(
                        typeof proportionOfOpenedWork !== "undefined"
                          ? proportionOfOpenedWork
                          : indicators.proportionOfOpenedWork,
                      )}
                      %
                    </p>
                  ),
                  workload_allocatedTime: () => {
                    const total =
                      typeof totalSumOfTimeAllocatedOnTheProject !== "undefined"
                        ? totalSumOfTimeAllocatedOnTheProject
                        : indicators.totalSumOfTimeAllocatedOnTheProject;

                    return (
                      <p
                        className={classNames(styles.text, {
                          [styles.warning]: total < 0,
                        })}
                      >
                        {toFixed(total)} <AllocationUnit />
                      </p>
                    );
                  },
                  workload_spentTime: () => {
                    const total =
                      typeof totalOfTimesSpentInProject !== "undefined"
                        ? totalOfTimesSpentInProject
                        : indicators.totalOfTimesSpentInProject;

                    return (
                      <p
                        className={classNames(styles.text, {
                          [styles.warning]: total < 0,
                        })}
                      >
                        {toFixed(total)} <AllocationUnit />
                      </p>
                    );
                  },
                  workload_remainingWork: () => {
                    const total =
                      typeof totalOfRemainingTimeInProject !== "undefined"
                        ? totalOfRemainingTimeInProject
                        : indicators.totalOfRemainingTimeInProject;

                    return (
                      <p
                        className={classNames(styles.text, {
                          [styles.warning]: total < 0,
                        })}
                      >
                        {toFixed(total)} <AllocationUnit />
                      </p>
                    );
                  },
                  budget_plannedBudget: () => {
                    const value =
                      typeof finalPlannedValueOfProject !== "undefined"
                        ? finalPlannedValueOfProject
                        : indicators.finalPlannedValueOfProject;

                    return <p className={styles.text}>{toFixed(value)}€</p>;
                  },
                  budget_realCosts: () => {
                    const realCost =
                      typeof trackingRealCost !== "undefined"
                        ? trackingRealCost
                        : indicators.trackingRealCost;
                    const value =
                      typeof finalPlannedValueOfProject !== "undefined"
                        ? finalPlannedValueOfProject
                        : indicators.finalPlannedValueOfProject;

                    return (
                      <p
                        className={classNames(styles.text, {
                          [styles.warning]: realCost > value,
                        })}
                      >
                        {toFixed(realCost)}€
                      </p>
                    );
                  },
                  budget_acquiredValue: () => {
                    const value =
                      typeof trackingAcquiredValue !== "undefined"
                        ? trackingAcquiredValue
                        : indicators.trackingAcquiredValue;

                    return <p className={styles.text}>{toFixed(value)}€</p>;
                  },
                  budget_allocatedBudget: () => {
                    const value =
                      typeof allocatedBudgetValue !== "undefined"
                        ? allocatedBudgetValue
                        : indicators.allocatedBudget;

                    return <p className={styles.text}>{toFixed(value)}€</p>;
                  },
                  budget_realMargin: () => {
                    const value =
                      typeof realMarginBudget !== "undefined"
                        ? realMarginBudget
                        : indicators.realMarginBudget;

                    return (
                      <p
                        className={classNames(styles.text, {
                          [styles.warning]: value < 0,
                          [styles.positive]: value > 0,
                        })}
                      >
                        {toFixed(value)}€
                      </p>
                    );
                  },
                  budget_plannedMargin: () => {
                    const value =
                      typeof plannedMarginBudget !== "undefined"
                        ? plannedMarginBudget
                        : indicators.plannedMarginBudget;

                    return (
                      <p
                        className={classNames(styles.text, {
                          [styles.warning]: value < 0,
                          [styles.positive]: value > 0,
                        })}
                      >
                        {toFixed(value)}€
                      </p>
                    );
                  },
                  projectManagers: () => (
                    <div className={styles.projectManagers} />
                  ),
                };

                return (
                  <div className={styles.projectSection}>
                    <div className={classNames(styles.labels, styles.titles)}>
                      <h3
                        className={styles.label}
                        // style={{ minWidth: "28rem", width: 0 }}
                        style={{ minWidth: getColSize("name"), width: 0 }}
                      />
                      {columns
                        .reduce<
                          ProjectListingConfiguration[number]["elements"]
                        >(
                          (elements, element) => [
                            ...elements,
                            ...(element.type === "section"
                              ? element.elements
                              : [element]),
                          ],
                          [],
                        )
                        .map((element) => {
                          const criteria = template.find(
                            ({ uuid }) => uuid === element.key,
                          );

                          return criteria && onColumnSort ? (
                            <h3
                              key={element.key}
                              className={classNames(
                                styles.label,
                                styles.sortable,
                                styles[element.key],
                              )}
                              // style={{
                              //   minWidth: "10rem",
                              //   width: 0,
                              //   cursor: "pointer",
                              // }}
                              style={{
                                minWidth: getColSize(element.key),
                                width: 0,
                                cursor: "pointer",
                              }}
                              onClick={() => onSortChange(criteria.uuid)}
                            >
                              <span>
                                {criteria.labels[currentLanguage] ||
                                  criteria.name}
                              </span>
                              <Icon
                                className={styles.icon}
                                isAscending={
                                  currentSort?.columnKey === criteria.uuid &&
                                  currentSort?.direction === "asc"
                                }
                                isDescending={
                                  currentSort?.columnKey === criteria.uuid &&
                                  currentSort?.direction === "desc"
                                }
                              />
                            </h3>
                          ) : (
                            <h3
                              key={element.key}
                              className={classNames(
                                styles.label,
                                styles[element.key],
                              )}
                              // style={{ minWidth: `10rem`, width: 0 }}
                              style={{
                                minWidth: getColSize(element.key),
                                width: 0,
                              }}
                            >
                              {!!criteria
                                ? criteria.labels[currentLanguage]
                                  ? criteria.labels[currentLanguage]
                                  : criteria.name
                                : element.type === "section"
                                  ? element.label[currentLanguage]
                                    ? element.label[currentLanguage]
                                    : t(
                                        `projectSection.columnNames.${element.key}`,
                                        element.key,
                                      )
                                  : t(
                                      `projectSection.columnNames.${element.key}`,
                                      element.key,
                                    )}
                            </h3>
                          );
                        })}
                    </div>
                    <div
                      className={classNames(styles.main, {
                        [styles.opened]: isOpen,
                      })}
                    >
                      {isOpen && (
                        <React.Fragment>
                          {loader}
                          <ul className={styles.projects}>
                            {filtered.map((project, index) => (
                              <li key={project.uuid} className={styles.project}>
                                <Project
                                  project={project}
                                  profile={{ ...profile, elements: columns }}
                                  colNbr={colNbr}
                                  areProjectStatsHiddenInProjectListing={
                                    settings.areProjectStatsHiddenInProjectListing &&
                                    !isAdministrator
                                  }
                                  getColSize={getColSize}
                                />
                              </li>
                            ))}
                          </ul>
                          <div className={styles.labels}>
                            <Resizeable
                              resize={
                                resizeCol
                                  ? (size) => resizeCol("name", size)
                                  : undefined
                              }
                              done={resizeDone}
                            >
                              {(rootRef, handle) => (
                                <h3
                                  ref={rootRef}
                                  className={styles.label}
                                  // style={{ minWidth: "20%", width: 0 }}
                                  // style={{ minWidth: "28rem", width: 0 }}
                                  style={{
                                    position: "relative",
                                    minWidth: getColSize("name"),
                                    width: 0,
                                  }}
                                >
                                  {t("projectSection.projectName")}
                                  {handle}
                                </h3>
                              )}
                            </Resizeable>
                            {columns.map((element) => {
                              const criteria = template.find(
                                ({ uuid }) => uuid === element.key,
                              );

                              return element.type === "section" ? (
                                <div
                                  key={element.key}
                                  className={styles.labelGroup}
                                  // style={{
                                  //   minWidth:
                                  //     element.type === "section"
                                  //       ? `${10 * element.elements.length}rem`
                                  //       : `10rem`,
                                  //   width: 0,
                                  // }}
                                  style={{
                                    minWidth:
                                      element.type === "section"
                                        ? element.elements.reduce(
                                            (total, { key }) =>
                                              total + getColSize(key),
                                            1,
                                          )
                                        : getColSize(element.key),
                                    width: 0,
                                  }}
                                >
                                  <h3
                                    className={classNames(
                                      styles.label,
                                      styles[element.key],
                                    )}
                                  >
                                    {element.type === "section"
                                      ? element.label[currentLanguage]
                                        ? element.label[currentLanguage]
                                        : t(
                                            `projectSection.columnNames.${element.key}`,
                                            element.key,
                                          )
                                      : t(
                                          `projectSection.columnNames.${element.key}`,
                                          element.key,
                                        )}
                                  </h3>
                                  <div className={styles.labels}>
                                    {element.elements.map((child) => {
                                      const criteria = template.find(
                                        ({ uuid }) => uuid === child.key,
                                      );

                                      return (
                                        <Resizeable
                                          key={child.key}
                                          resize={
                                            resizeCol
                                              ? (size) =>
                                                  resizeCol(child.key, size)
                                              : undefined
                                          }
                                          done={resizeDone}
                                        >
                                          {(rootRef, handle) => (
                                            <h3
                                              ref={rootRef}
                                              className={classNames(
                                                styles.label,
                                                styles[element.key],
                                              )}
                                              // style={{
                                              //   minWidth: `${
                                              //     100 / element.elements.length
                                              //   }%`,
                                              //   width: 0,
                                              // }}
                                              style={{
                                                position: "relative",
                                                minWidth: getColSize(child.key),
                                                width: 0,
                                              }}
                                            >
                                              {!!criteria
                                                ? criteria.labels[
                                                    currentLanguage
                                                  ]
                                                  ? criteria.labels[
                                                      currentLanguage
                                                    ]
                                                  : criteria.name
                                                : t(
                                                    `projectSection.columnNames.${child.key}`,
                                                    child.key,
                                                  )}
                                              {handle}
                                            </h3>
                                          )}
                                        </Resizeable>
                                      );
                                    })}
                                  </div>
                                </div>
                              ) : (
                                <Resizeable
                                  key={element.key}
                                  resize={
                                    resizeCol
                                      ? (size) => resizeCol(element.key, size)
                                      : undefined
                                  }
                                  done={resizeDone}
                                >
                                  {(rootRef, handle) => (
                                    <h3
                                      ref={rootRef}
                                      className={classNames(
                                        styles.label,
                                        styles[element.key],
                                      )}
                                      // style={{
                                      //   minWidth: `10rem`,
                                      //   width: 0,
                                      // }}
                                      style={{
                                        position: "relative",
                                        minWidth: getColSize(element.key),
                                        width: 0,
                                      }}
                                    >
                                      {!!criteria
                                        ? criteria.labels[currentLanguage]
                                          ? criteria.labels[currentLanguage]
                                          : criteria.name
                                        : t(
                                            `projectSection.columnNames.${element.key}`,
                                            element.key,
                                          )}
                                      {handle}
                                    </h3>
                                  )}
                                </Resizeable>
                              );
                            })}
                          </div>
                        </React.Fragment>
                      )}
                      <div className={styles.header}>
                        <Resizeable
                          resize={
                            resizeCol
                              ? (size) => resizeCol("name", size)
                              : undefined
                          }
                          done={resizeDone}
                        >
                          {(rootRef, handle) => (
                            <div
                              ref={rootRef}
                              className={styles.zoneInfo}
                              // style={{ minWidth: "20%", width: 0 }}
                              // style={{ minWidth: "28rem", width: 0 }}
                              style={{
                                position: "relative",
                                minWidth: getColSize("name"),
                                width: 0,
                              }}
                            >
                              {header(filtered)}
                              {handle}
                            </div>
                          )}
                        </Resizeable>
                        {columns
                          .reduce<
                            ProjectListingConfiguration[number]["elements"]
                          >(
                            (elements, element) => [
                              ...elements,
                              ...(element.type === "section"
                                ? element.elements
                                : [element]),
                            ],
                            [],
                          )
                          .map((element) => {
                            const criteria = template.find(
                              ({ uuid }) => uuid === element.key,
                            );

                            return (
                              <Resizeable
                                key={element.key}
                                resize={
                                  resizeCol
                                    ? (size) => resizeCol(element.key, size)
                                    : undefined
                                }
                                done={resizeDone}
                              >
                                {(rootRef, handle) => (
                                  <div
                                    ref={rootRef}
                                    className={classNames(
                                      styles.col,
                                      styles[element.key],
                                    )}
                                    // style={{
                                    //   minWidth: `${80 / colNbr}%`,
                                    //   width: 0,
                                    // }}
                                    // style={{
                                    //   minWidth: `10rem`,
                                    //   width: 0,
                                    // }}
                                    style={{
                                      position: "relative",
                                      minWidth: getColSize(element.key),
                                      width: 0,
                                    }}
                                  >
                                    {!!criteria ? (
                                      <p className={styles.text}>
                                        {typeof indicators[
                                          criteria.uuid as keyof typeof indicators
                                        ] !== "undefined"
                                          ? indicators[
                                              criteria.uuid as keyof typeof indicators
                                            ]
                                          : ""}
                                      </p>
                                    ) : !!fixedColMap[
                                        element.key as keyof typeof fixedColMap
                                      ] ? (
                                      fixedColMap[
                                        element.key as keyof typeof fixedColMap
                                      ]()
                                    ) : (
                                      <p className={styles.text}>
                                        {t(
                                          `projectSection.columnNames.${element.key}`,
                                          element.key,
                                        )}
                                      </p>
                                    )}
                                    {handle}
                                  </div>
                                )}
                              </Resizeable>
                            );
                          })}
                      </div>
                    </div>
                  </div>
                );
              }}
            </Filter>
          )}
        </Language>
      )}
    </RestrictedArea>
  );
}

export { ProjectSection };
